/**
 * The two things the request form owes a reader before they ask for anything:
 * what period is actually about to be read, and what an empty one means.
 *
 * **They live here, apart from the form, for the reason slice 12 split its
 * WhatsApp panels.** `RequestReportPanel` imports the API client, which imports
 * `lib/config.ts`, which reads `import.meta.env` and therefore only evaluates
 * inside Vite — so copy that stayed in that file could not be rendered by
 * `scripts/verify-report-screens.tsx` and would be checked by nobody. These
 * sentences are the deliverable, so they sit where a render test can reach them.
 *
 * **The period preview exists because a picker is not a statement.** Somebody
 * choosing "the month that just ended" or typing two dates has told the screen
 * what they meant; this block tells them back what will be *stored on the run*
 * — both boundaries, spelled "up to but not including", in the zone they were
 * chosen in. The alternative, showing the two dates they typed, agrees with the
 * form and not necessarily with the report: a window is half-open and its upper
 * bound is the day after the last day anybody asked for.
 *
 * **The empty-period note exists because a small file looks like a broken one.**
 * A period in which nothing moved is a successful report of a quiet period —
 * there is deliberately no `no_data` failure code — and the workbook says so on
 * its own sheets in the sentence quoted here, imported rather than re-typed so
 * the screen cannot promise wording the file does not use. This app cannot say
 * whether any *particular* finished run turned out empty: a run record carries
 * no row count, and guessing from a byte size would be exactly the invention
 * this codebase keeps catching. So the fact is explained once, where a period is
 * being chosen, and never claimed about a specific row.
 *
 * **Slice 17 shortened these and removed nothing.** Every sentence here is a
 * statement about a boundary or about a state, and a reader who does not have it
 * draws a false conclusion — that the report covers a day it does not, or that a
 * small file is a broken one. What went was the trailing justification on each:
 * *why* an exclusive bound stops two reports counting the same credit twice is
 * an argument for the next builder and is in this header, while *that* the
 * closing instant belongs to the next period is a fact about the file somebody
 * is about to receive and stays on the screen.
 *
 * **Slice 44: the closing instant is said about the kind's own things.** A
 * credit movement for credit usage; a campaign for Results by surface, which
 * reads campaigns by the instant they were sent and has no movements at all.
 */
import { NO_MOVEMENTS_NOTE, describeReportWindow, type ReportKind, type ReportWindow } from '@wend/shared';
import { CalendarRange } from 'lucide-react';

import { reportWindowLengthLabel } from '../../lib/report-display';

export interface ReportPeriodPreviewProps {
  /** The window as it will be sent, or `null` when it could not be established. */
  period: ReportWindow | null;
  /** The zone the boundaries were chosen in — named even when it was a fallback. */
  timezone: string;
  /** False when `Intl` would not name this browser's zone. */
  zoneResolved: boolean;
  /** True for the two recurring rules, false for hand-picked dates. */
  namedPeriod: boolean;
  /** Whose things the closing instant is said about. Credit usage when not given. */
  kind?: ReportKind;
}

/** What belongs to the next period at exactly the closing instant, per kind. */
const CLOSING_INSTANT: Readonly<Record<ReportKind, string>> = {
  credit_usage: 'A movement at exactly the closing instant belongs to the next period, never to this one.',
  surface_results: 'A campaign sent at exactly the closing instant belongs to the next period, never to this one.',
};

export function ReportPeriodPreview({
  period,
  timezone,
  zoneResolved,
  namedPeriod,
  kind = 'credit_usage',
}: ReportPeriodPreviewProps) {
  return (
    <div className="flex flex-col gap-2 rounded-base border border-line bg-white/[0.02] px-4 py-3.5">
      <span className="flex items-center gap-2 font-mono text-[0.66rem] uppercase tracking-[0.12em] text-muted">
        <CalendarRange aria-hidden="true" className="size-3.5" />
        What this report will cover
      </span>

      {period === null ? (
        <p className="text-[0.88rem] leading-relaxed text-muted">
          {namedPeriod
            ? `This browser could not work out ${timezone}, so there is no period to ask for. Nothing will be read against a guessed clock.`
            : 'Pick a first and a last day.'}
        </p>
      ) : (
        <>
          <p className="text-[0.9rem] leading-relaxed text-text">{describeReportWindow(period)}</p>
          <p className="text-[0.82rem] leading-snug text-muted">
            {reportWindowLengthLabel(period)}. {CLOSING_INSTANT[kind]}
          </p>
        </>
      )}

      {!zoneResolved && (
        <p className="text-[0.82rem] leading-snug text-muted">
          This browser would not say which timezone it is in, so {timezone} was assumed.
        </p>
      )}
    </div>
  );
}

/** What an empty period means, said before anybody has to wonder. */
export function ReportEmptyPeriodNote() {
  return (
    <p className="max-w-prose text-[0.85rem] leading-relaxed text-muted">
      A period in which nothing moved is a finished report, not a failure: each sheet says
      &ldquo;{NO_MOVEMENTS_NOTE}&rdquo;.
    </p>
  );
}
